/**
 * OpenAI Service V2
 * 
 * A simplified, focused approach to OpenAI integration that:
 * 1. Uses field-specific system prompts
 * 2. Only sends necessary data to the API
 * 3. Properly handles response parsing
 */
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { 
  generateTemplatePrompt, 
  parseTemplateResult,
  createCompletion as createOpenAICompletion,
  getSystemPromptForFields
} from './templateProcessingServiceV2';
import { getAIModelById, getDefaultAIModel } from './aiModelService';
import { processWithAIProvider } from './aiProviderService';
import { deductCredits } from './optimizedCreditService';
import { hasEnoughCreditsForAIModel } from './creditService';
import { countProductImages, generatePromptHint } from './imagePlaceholderService';

const OPENAI_SETTINGS_COLLECTION = 'openai_settings';

/**
 * Save OpenAI settings to Firestore
 * @param {string} userId - The user ID
 * @param {Object} settings - The OpenAI settings
 * @returns {Object} The saved settings
 */
export const saveOpenAISettings = async (userId, settings) => {
  try {
    if (!userId) throw new Error('User ID is required');
    
    console.log('Saving OpenAI settings for user:', userId);
    const settingsRef = doc(db, OPENAI_SETTINGS_COLLECTION, userId);
    await setDoc(settingsRef, {
      ...settings,
      updatedAt: new Date().toISOString()
    });

    console.log('Successfully saved OpenAI settings');
    return settings;
  } catch (error) {
    console.error('Error saving OpenAI settings:', error);
    throw error;
  }
};

/**
 * Get OpenAI settings from Firestore
 * @param {string} userId - The user ID
 * @returns {Object} The OpenAI settings
 */
export const getOpenAISettings = async (userId) => {
  try {
    if (!userId) {
      console.error('getOpenAISettings called with no userId');
      return null;
    }
    
    console.log('Getting OpenAI settings for user:', userId);
    const settingsRef = doc(db, OPENAI_SETTINGS_COLLECTION, userId);
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      console.log('Found OpenAI settings:', data);
      return data;
    } else {
      console.log('No OpenAI settings found for user:', userId);
      // Return null to trigger default settings creation
      return null;
    }
  } catch (error) {
    console.error('Error getting OpenAI settings:', error);
    throw error;
  }
};

/**
 * Create a completion with the OpenAI API
 * @param {string} apiKey - The OpenAI API key
 * @param {Object} params - The completion parameters
 * @param {string} baseUrl - Optional custom base URL for the API
 * @returns {Object} The OpenAI API response
 */
export const callOpenAICompletion = async (apiKey, { model, messages, temperature, max_tokens }, baseUrl) => {
  try {
    // Log the request
    console.log('%c[OpenAI Request]', 'color: #0066cc; font-weight: bold', {
      model,
      temperature,
      max_tokens,
      messages: JSON.parse(JSON.stringify(messages)) // Deep clone to avoid circular references
    });
    
    // Log the raw request for debugging
    console.log('%c[OpenAI Request - RAW]', 'color: #ff6600; font-weight: bold; font-size: 14px');
    console.log(JSON.stringify({
      model,
      messages,
      temperature,
      max_tokens
    }, null, 2));
    
    // Use custom base URL if provided, otherwise use the default OpenAI API URL
    const apiUrl = baseUrl ? `${baseUrl}/chat/completions` : 'https://api.openai.com/v1/chat/completions';
    console.log('Using API URL:', apiUrl);
    
    // Prepare the request
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model,
        messages,
        temperature,
        max_tokens
      })
    });
    
    // Parse the response
    const data = await response.json();
    
    // Log the response
    console.log('%c[OpenAI Response]', 'color: #00cc66; font-weight: bold', {
      status: response.status,
      statusText: response.statusText,
      data: JSON.parse(JSON.stringify(data)) // Deep clone to avoid circular references
    });
    
    // Log the raw response for debugging
    console.log('%c[OpenAI Response - RAW]', 'color: #ff6600; font-weight: bold; font-size: 14px');
    console.log(JSON.stringify(data, null, 2));
    
    // Check for errors
    if (!response.ok) {
      throw new Error(data.error?.message || `OpenAI API error: ${response.status} ${response.statusText}`);
    }
    
    return data;
  } catch (error) {
    console.error('Error creating OpenAI completion:', error);
    throw error;
  }
};

/**
 * Process a template with the selected AI model
 * @param {Object} template - The template configuration
 * @param {Object} productData - The filtered product data
 * @param {String} legacyApiKey - Legacy API key (for backward compatibility)
 * @param {Object} options - Additional options including userId and modelId
 * @returns {Object} The processed template result
 */
export const processTemplateWithOpenAI = async (template, productData, legacyApiKey, options = {}) => {
  const { userId, modelId, userModelSettings } = options;
  
  try {
    console.log('Processing template with AI model:', { templateName: template.name, modelId: modelId || template.modelId });
    
    // If we don't have a model ID, use the legacy method
    if (!modelId && !template.modelId) {
      if (legacyApiKey) {
        return await processTemplateWithOpenAILegacy(template, productData, legacyApiKey, userModelSettings);
      } else {
        return { error: 'No AI model specified and no legacy API key provided' };
      }
    }
    
    // Get the AI model
    const aiModelId = modelId || template.modelId;
    let aiModel = null;
    
    try {
      aiModel = await getAIModelById(aiModelId);
      if (!aiModel) {
        // If the specific model isn't found, try to get the default model
        aiModel = await getDefaultAIModel();
        if (!aiModel) {
          return { error: `AI model not found and no default model available` };
        }
        console.warn(`Model ${aiModelId} not found, using default model ${aiModel.id}`);
      }
    } catch (modelError) {
      console.error('Error getting AI model:', modelError);
      return { error: `Failed to get AI model: ${modelError.message}` };
    }
    
    console.log('Using AI model:', { 
      id: aiModel.id, 
      name: aiModel.displayName, 
      provider: aiModel.provider,
      creditCost: aiModel.creditCostPerOutput
    });
    
    // Calculate the credit cost
    const outputFieldCount = template.outputFields ? template.outputFields.length : 1;
    const creditCost = outputFieldCount * (aiModel.creditCostPerOutput || 1);
    
    console.log('Credit cost calculation:', {
      outputFieldCount,
      creditCostPerOutput: aiModel.creditCostPerOutput,
      totalCreditCost: creditCost
    });
    
    // Check if the user has enough credits
    if (userId) {
      const hasEnoughCredits = await hasEnoughCreditsForAIModel(userId, creditCost);
      if (!hasEnoughCredits) {
        return { error: `Not enough credits. You need ${creditCost} credits to process this template.` };
      }
    }
    
    // Process metafields in the prompt
    const processPromptWithMetafields = (prompt, productData) => {
      if (!prompt) return prompt;
      
      // Create a flattened version of metafields for easier access
      const flattenedMetafields = {};
      
      // First scan the prompt for metafield placeholders to know what we need to look for
      const metafieldRegex = /{([\w-]+)\.([\w-]+)}/g;
      const requiredMetafields = new Set();
      let match;
      
      console.log('Scanning prompt for metafield placeholders');
      while ((match = metafieldRegex.exec(prompt)) !== null) {
        const namespace = match[1]; // e.g., custom
        const key = match[2]; // e.g., googlekeywords
        const metafieldKey = `${namespace}.${key}`;
        requiredMetafields.add(metafieldKey);
        console.log(`Found metafield placeholder in prompt: ${metafieldKey}`);
      }
      
      // If we have metafield placeholders or inputMetafields, process them
      if (requiredMetafields.size > 0 || (productData.inputMetafields && Array.isArray(productData.inputMetafields))) {
        console.log('Processing metafields for prompt placeholders');
        
        // Collect all metafields we need to process (from both placeholders and inputMetafields)
        const metafieldsToProcess = new Set(requiredMetafields);
        
        // Add inputMetafields to the processing list
        if (productData.inputMetafields && Array.isArray(productData.inputMetafields)) {
          console.log('Found inputMetafields in productData:', productData.inputMetafields);
          
          productData.inputMetafields.forEach(metafield => {
            if (metafield && metafield.namespace && metafield.key) {
              const metafieldKey = `${metafield.namespace}.${metafield.key}`;
              metafieldsToProcess.add(metafieldKey);
            }
          });
        }
        
        // Log product data structure for debugging
        console.log('Raw product data type:', typeof productData);
        console.log('Product data object keys:', Object.keys(productData));
        
        // Check if we have the product in window.localEdits
        if (typeof window !== 'undefined' && window.localEdits && productData.id) {
          const productId = productData.id.toString();
          console.log(`Checking window.localEdits for product ${productId}`);
          
          if (window.localEdits[productId]) {
            console.log(`Found product ${productId} in window.localEdits`);
            console.log('Local edits for this product:', window.localEdits[productId]);
            
            // Process each metafield we need
            for (const metafieldKey of metafieldsToProcess) {
              const [namespace, key] = metafieldKey.split('.');
              console.log(`Looking for metafield ${metafieldKey} - checking sources in priority order`);
              
              // PRIORITY 1: Check if the product has a metafields object with the metafield (current data)
              if (productData.metafields && productData.metafields[metafieldKey] !== undefined) {
                flattenedMetafields[metafieldKey] = productData.metafields[metafieldKey];
                console.log(`Found metafield ${metafieldKey} in product.metafields with value:`, flattenedMetafields[metafieldKey]);
                continue; // Skip to next metafield if found
              }
              
              // PRIORITY 2: Check metafield_updates (recent changes)
              if (window.localEdits[productId].metafield_updates && 
                  window.localEdits[productId].metafield_updates[metafieldKey] !== undefined) {
                flattenedMetafields[metafieldKey] = window.localEdits[productId].metafield_updates[metafieldKey];
                console.log(`Found metafield ${metafieldKey} in window.localEdits.metafield_updates with value:`, 
                          window.localEdits[productId].metafield_updates[metafieldKey]);
                continue; // Skip to next metafield if found
              }
              
              // PRIORITY 3: Check metafields_array
              if (window.localEdits[productId].metafields_array && 
                  Array.isArray(window.localEdits[productId].metafields_array)) {
                const metafieldInArray = window.localEdits[productId].metafields_array.find(
                  m => m.namespace === namespace && m.key === key
                );
                
                if (metafieldInArray && metafieldInArray.value !== undefined && metafieldInArray.value !== null) {
                  flattenedMetafields[metafieldKey] = metafieldInArray.value;
                  console.log(`Found metafield ${metafieldKey} in window.localEdits.metafields_array with value:`, 
                            metafieldInArray.value);
                  continue; // Skip to next metafield if found
                }
              }
              
              // PRIORITY 4: Check metafields object (only if not null)
              if (window.localEdits[productId].metafields && 
                  window.localEdits[productId].metafields[metafieldKey]) {
                const value = window.localEdits[productId].metafields[metafieldKey].value || 
                            window.localEdits[productId].metafields[metafieldKey];
                if (value !== undefined && value !== null) {
                  flattenedMetafields[metafieldKey] = value;
                  console.log(`Found metafield ${metafieldKey} in window.localEdits.metafields with value:`, value);
                  continue; // Skip to next metafield if found
                }
              }
              
              // If we reach here, we couldn't find the metafield value anywhere
              console.log(`No value found for metafield ${metafieldKey} in any source, setting empty string`);
              flattenedMetafields[metafieldKey] = '';
            }
          } else {
            console.log(`Product ${productId} not found in window.localEdits`);
            
            // Set empty values for all required metafields
            for (const metafieldKey of metafieldsToProcess) {
              console.log(`No local edits found, setting empty value for ${metafieldKey}`);
              flattenedMetafields[metafieldKey] = '';
            }
          }
        } else {
          console.log('window.localEdits not available or product ID missing');
          
          // Set empty values for all required metafields
          for (const metafieldKey of metafieldsToProcess) {
            console.log(`No local edits context, setting empty value for ${metafieldKey}`);
            flattenedMetafields[metafieldKey] = '';
          }
        }
      }
      
      // Replace all metafield placeholders in the prompt
      console.log('Replacing metafield placeholders in prompt');
      metafieldRegex.lastIndex = 0; // Reset regex index
      while ((match = metafieldRegex.exec(prompt)) !== null) {
        const fullMatch = match[0]; // e.g., {custom.googlekeywords}
        const namespace = match[1]; // e.g., custom
        const key = match[2]; // e.g., googlekeywords
        const metafieldKey = `${namespace}.${key}`;
        
        // Get the value from our flattened metafields (default to empty string)
        let value = flattenedMetafields[metafieldKey] || '';
        
        // If the value is an object with a value property, extract the actual value
        if (value && typeof value === 'object' && value.value !== undefined) {
          value = value.value;
        }
        
        console.log(`Replacing placeholder ${fullMatch} with value:`, value);
        
        // Replace all instances of this placeholder
        prompt = prompt.replace(new RegExp(escapeRegExp(fullMatch), 'g'), value);
      }
      
      // Replace standard placeholders in the prompt with actual values
      for (const key in productData) {
        if (key !== 'metafields' && key !== 'inputMetafields') {
          const placeholder = `{${key}}`;
          const value = productData[key];
          prompt = prompt.replace(new RegExp(escapeRegExp(placeholder), 'g'), value || '');
        }
      }
      
      console.log('%c[Final Processed Prompt]', 'color: #ff6600; font-weight: bold; font-size: 14px');
      console.log(prompt);
      
      return prompt;
    };
    
    // Prepare the prompt
    let prompt = template.prompt;
    prompt = processPromptWithMetafields(prompt, productData);
    
    // Add product data directly to the prompt if it's not already included
    if (Object.keys(productData).length > 0 && !prompt.includes('PRODUCT DATA')) {
      console.log('%c[Processing Product Data for Prompt]', 'color: #6600cc; font-weight: bold; font-size: 12px');
      console.log('Raw Product Data:', productData);
      
      // Format product data as a clean string
      let productDataString = '\n\n### PRODUCT DATA ###\n';
      
      // Add each field from the product data
      Object.keys(productData).forEach(key => {
        if (key !== 'metafields' && key !== 'id') {
          const value = productData[key];
          if (typeof value === 'string') {
            productDataString += `\n${key}: ${value}`;
          } else if (value !== undefined) {
            // For non-string values, try to stringify them
            try {
              productDataString += `\n${key}: ${JSON.stringify(value)}`;
            } catch (e) {
              productDataString += `\n${key}: [Complex data]`;
            }
          }
        }
      });
      
      // Add metafields if they exist
      if (productData.metafields && Object.keys(productData.metafields).length > 0) {
        productDataString += '\n\n### METAFIELDS ###';
        Object.keys(productData.metafields).forEach(key => {
          productDataString += `\n${key}: ${productData.metafields[key]}`;
        });
      }
      
      // Append the product data to the prompt
      prompt += productDataString;
      
      console.log('%c[Added Product Data to Prompt]', 'color: #3366cc; font-weight: bold', {
        promptLength: prompt.length,
        includesProductData: true,
        fieldCount: Object.keys(productData).filter(k => k !== 'metafields' && k !== 'id').length,
        metafieldCount: productData.metafields ? Object.keys(productData.metafields).length : 0
      });
      
      console.log('%c[Complete Prompt with Product Data]', 'color: #009900; font-weight: bold; font-size: 14px');
      console.log(prompt);
    }
    
    // === IMAGE PLACEHOLDER HINT ===
    // If the template has image placeholders enabled, append image count hint
    if (template.enableImagePlaceholders) {
      let imageCount = template._productImageCount || 0;
      const mode = template.imagePlaceholderMode || 'manual';
      const imageAnalyses = template._imageAnalyses || null;
      const hint = generatePromptHint(imageCount, mode, imageAnalyses);
      prompt += hint;
      
      console.log('%c[Image Placeholder Hint Added to Prompt]', 'color: #e67e22; font-weight: bold', {
        imageCount,
        mode,
        hintLength: hint.length
      });
    }
    
    // Add instructions for metafields if the template has output metafields
    if (template.outputMetafields && template.outputMetafields.length > 0) {
      // Extract metafield keys from the template
      const metafieldKeys = template.outputMetafields.map(m => {
        if (typeof m === 'object' && m.namespace && m.key) {
          return `${m.namespace}.${m.key}`;
        } else if (typeof m === 'string' && m.includes('.')) {
          return m;
        }
        return null;
      }).filter(Boolean);
      
      if (metafieldKeys.length > 0) {
        // Add explicit instructions to use the full metafield key
        prompt += `\n\nIMPORTANT: Your response MUST use the EXACT metafield key format in your JSON response.\n`;
        prompt += `For example, use "${metafieldKeys[0]}" as the field name in your JSON response, NOT "${metafieldKeys[0].split('.')[1]}".\n`;
        prompt += `\nExpected metafield keys in your response:\n`;
        metafieldKeys.forEach(key => {
          prompt += `- "${key}"\n`;
        });
      }
    }
    
    // === NAME POOL INSTRUCTIONS ===
    let outputFieldsToProcess = template.outputFields ? [...template.outputFields] : [];
    if (template.namePoolId) {
      prompt += `\n\n### NAME GENERATION (NAME POOL) ###\n`;
      prompt += `You MUST generate a unique name for this product.\n`;
      
      if (template._forbiddenNames && template._forbiddenNames.length > 0) {
        prompt += `DO NOT use any of the following names, they are already taken:\n`;
        prompt += template._forbiddenNames.join(', ') + `\n\n`;
      }
      
      prompt += `IMPORTANT: You MUST include the generated name in your JSON response under the exact key "generated_name".\n`;
      
      console.log('%c[Name Pool Instructions Added to Prompt]', 'color: #e67e22; font-weight: bold', {
        hasForbiddenNames: !!template._forbiddenNames,
        forbiddenCount: template._forbiddenNames ? template._forbiddenNames.length : 0
      });
      
      if (!outputFieldsToProcess.includes('generated_name')) {
        outputFieldsToProcess.push('generated_name');
      }
    }
    
    // Generate system prompt based on output fields
    const systemPrompt = getSystemPromptForFields(outputFieldsToProcess, template.outputMetafields || []);
    
    console.log('%c[Generated System Prompt]', 'color: #9932cc; font-weight: bold');
    console.log(systemPrompt);
    
    // Process the prompt with the AI provider
    console.log('%c[Sending to AI Provider]', 'color: #cc0066; font-weight: bold; font-size: 14px');
    console.log('Provider:', aiModel.provider);
    console.log('Model ID:', aiModel.modelId);
    console.log('Temperature:', aiModel.temperature || 0.7);
    console.log('Max Tokens:', aiModel.maxTokens || 2000);
    console.log('%c[Final User Prompt Sent to AI]', 'color: #cc0066; font-weight: bold; font-size: 12px');
    console.log(prompt);
    
    let result;
    try {
      result = await processWithAIProvider({
        provider: aiModel.provider,
        modelId: aiModel.modelId,
        systemMessage: systemPrompt,
        prompt,
        temperature: aiModel.temperature || 0.7,
        maxTokens: aiModel.maxTokens || 2000,
        userModelSettings,
        apiConfig: aiModel.apiConfig // Pass model's API config (includes API key)
      });
      
      console.log('%c[AI Provider Response]', 'color: #0066cc; font-weight: bold; font-size: 14px');
      console.log(result);
    } catch (providerError) {
      console.error('Error from AI provider:', providerError);
      return { error: `AI provider error: ${providerError.message}` };
    }
    
    // Parse the result
    let parsedResult;
    let initialParseError = null;
    
    // First try to parse as raw JSON
    try {
      parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
    } catch (error) {
      initialParseError = error;
      
      // If parsing fails, check if it's a markdown-formatted response
      const markdownMatch = result.match(/```(?:json)?([\s\S]*?)```/);
      if (markdownMatch) {
        // Extract the content between the markdown code blocks
        const jsonContent = markdownMatch[1].trim();
        try {
          console.log('Extracted JSON from markdown:', jsonContent);
          parsedResult = JSON.parse(jsonContent);
          // Successfully parsed markdown JSON, clear the error
          initialParseError = null;
        } catch (markdownError) {
          console.error('Error parsing extracted markdown content:', markdownError);
        }
      }
      
      // If we still have an error and couldn't parse markdown, try to find any JSON object
      if (initialParseError) {
        try {
          const jsonMatch = result.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            parsedResult = JSON.parse(jsonMatch[0]);
            // Successfully parsed JSON object, clear the error
            initialParseError = null;
          } else {
            throw new Error('Could not extract JSON from result');
          }
        } catch (extractError) {
          console.error('Error extracting JSON from result:', extractError);
          return { error: 'Failed to parse AI model response as JSON. Please check the template prompt.' };
        }
      }
    }
    
    // If we still have an error after all parsing attempts, return the error
    if (initialParseError) {
      console.error('Error parsing result as JSON:', initialParseError);
      return { error: 'Failed to parse AI model response as JSON. Please check the template prompt.' };
    }
    
    // Only log credit usage if we successfully parsed the result
    if (userId) {
      try {
        // Log credit usage but don't deduct (deduction happens in templateExecutionServiceV2.js)
        console.log(`Credit usage logged for user ${userId} - ${creditCost} credits (actual deduction happens in template execution service)`);
      } catch (creditError) {
        console.error('Error logging credit usage:', creditError);
        return { 
          ...parsedResult, 
          warning: `Template executed successfully but there was an issue with credit tracking: ${creditError.message}` 
        };
      }
    }
    
    return parsedResult;
  } catch (error) {
    console.error('Error processing template with AI model:', error);
    return { error: error.message || 'Unknown error processing template' };
  }
};

/**
 * Legacy method for processing templates with OpenAI
 * This is used for backward compatibility with templates that don't have a modelId
 */
const processTemplateWithOpenAILegacy = async (template, productData, apiKey, userModelSettings) => {
  if (!apiKey) {
    return { error: 'OpenAI API key is required' };
  }
  
  try {
    console.log('Using legacy OpenAI processing method');
    
    // Prepare the prompt
    let prompt = template.prompt;
    
    // Try to get metafield values from browser context if available
    try {
      // Create a flattened version of metafields for easier access
      const flattenedMetafields = {};
      
      // Check if there's a special inputMetafields property
      if (productData.inputMetafields && Array.isArray(productData.inputMetafields)) {
        console.log('Found inputMetafields in productData:', productData.inputMetafields);
        
        // Simple logging of the raw data
        console.log('Raw product data type:', typeof productData);
        console.log('Product data object keys:', Object.keys(productData));
        
        // If there's a nested productData property that's a string, log it
        if (productData.productData && typeof productData.productData === 'string') {
          console.log('Found nested productData string. First 200 chars:', productData.productData.substring(0, 200) + '...');
        }
        
        // For each input metafield, try to find its value in window.localEdits
        productData.inputMetafields.forEach(metafield => {
          if (metafield && metafield.namespace && metafield.key) {
            const metafieldKey = `${metafield.namespace}.${metafield.key}`;
            
            // Only check window.localEdits if we don't already have a value
            if (!flattenedMetafields[metafieldKey] && typeof window !== 'undefined' && window.localEdits) {
              console.log(`Looking for metafield ${metafieldKey} in window.localEdits`);
              
              // Find the product in localEdits
              const productId = productData.id.toString();
              if (window.localEdits[productId]) {
                console.log(`Found product ${productId} in window.localEdits`);
                
                // Check metafields object (primary storage for UI editor values)
                if (window.localEdits[productId].metafields && 
                    window.localEdits[productId].metafields[metafieldKey]) {
                  const value = window.localEdits[productId].metafields[metafieldKey].value || 
                               window.localEdits[productId].metafields[metafieldKey];
                  if (value !== undefined) {
                    flattenedMetafields[metafieldKey] = value;
                    console.log(`Found metafield ${metafieldKey} in window.localEdits.metafields with value:`, value);
                  }
                }
                
                // Check metafield_updates (used for API compatibility)
                if (!flattenedMetafields[metafieldKey] && 
                    window.localEdits[productId].metafield_updates && 
                    window.localEdits[productId].metafield_updates[metafieldKey] !== undefined) {
                  flattenedMetafields[metafieldKey] = window.localEdits[productId].metafield_updates[metafieldKey];
                  console.log(`Found metafield ${metafieldKey} in metafield_updates with value:`, 
                             window.localEdits[productId].metafield_updates[metafieldKey]);
                }
                
                // Check metafields_array (used for GraphQL API compatibility)
                if (!flattenedMetafields[metafieldKey] && 
                    window.localEdits[productId].metafields_array && 
                    Array.isArray(window.localEdits[productId].metafields_array)) {
                  const metafieldInArray = window.localEdits[productId].metafields_array.find(
                    m => m.namespace === metafield.namespace && m.key === metafield.key
                  );
                  
                  if (metafieldInArray && metafieldInArray.value !== undefined) {
                    flattenedMetafields[metafieldKey] = metafieldInArray.value;
                    console.log(`Found metafield ${metafieldKey} in metafields_array with value:`, metafieldInArray.value);
                  }
                }
              }
            }
            
            // If we still don't have a value, log it
            if (!flattenedMetafields[metafieldKey]) {
              console.log(`No value found for metafield ${metafieldKey} in any source`);
            }
          }
        });
      }
      
      // Find and replace all metafield placeholders in the format {namespace.key}
      const metafieldRegex = /{([\w-]+)\.([\w-]+)}/g;
      let match;
      
      console.log('Searching for metafield placeholders in prompt');
      while ((match = metafieldRegex.exec(prompt)) !== null) {
        const fullMatch = match[0]; // e.g., {custom.googlekeywords}
        const namespace = match[1]; // e.g., custom
        const key = match[2]; // e.g., googlekeywords
        const metafieldKey = `${namespace}.${key}`;
        
        console.log(`Found metafield placeholder: ${fullMatch}, key: ${metafieldKey}`);
        
        // Get the value from our flattened metafields
        const value = flattenedMetafields[metafieldKey] || '';
        console.log(`Replacing placeholder ${fullMatch} with value: ${value}`);
        
        // Replace all instances of this placeholder
        prompt = prompt.replace(new RegExp(escapeRegExp(fullMatch), 'g'), value);
      }
      
      // Replace standard placeholders in the prompt with actual values
      for (const key in productData) {
        if (key !== 'metafields' && key !== 'inputMetafields') {
          const placeholder = `{${key}}`;
          const value = productData[key];
          prompt = prompt.replace(new RegExp(placeholder, 'g'), value || '');
        }
      }
      
      console.log('Final prompt:', prompt.substring(0, 200) + '...');
    } catch (error) {
      console.error('Error processing metafields:', error);
    }
    
    // Generate the system prompt and user prompt
    const { prompt: formattedPrompt, systemPrompt } = generateTemplatePrompt(template, productData);
    
    // Get the base URL from user settings if available
    const baseUrl = userModelSettings?.openai?.baseUrl || null;
    
    // Use the callOpenAICompletion function
    const response = await callOpenAICompletion(apiKey, {
      model: 'gpt-3.5-turbo',
      messages: [
        {
          role: 'system',
          content: 'You are a helpful assistant that generates product content based on the provided information. Respond with valid JSON only.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      temperature: 0.7,
      maxTokens: 2000
    }, baseUrl);
    
    // Parse the result
    const result = parseTemplateResult(response);
    return result;
  } catch (error) {
    console.error('Error processing template with OpenAI:', error);
    return { error: error.message || 'Unknown error processing template' };
  }
};

// Helper function to escape special characters in regex
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}
